import { Component, h, Prop, Host, State, Watch, Element } from "@stencil/core";
import { QuickOrderResponse } from "./quick-order-response";
import api from "../../utils/api";
import { buildUrl } from "../../utils/url";
import { debounce } from "../../utils/debounce";
import { ButtonSize, ButtonVariant } from "@trumpf/ux.ui/dist/types/components";

@Component({
    tag: "ux-w-quick-order",
    styleUrl: "quick-order.scss",
    shadow: false
})
export class QuickOrder {
    /** URL where this widget can request the data */
    @Prop() url: string = "";
    /** This holds the response from the API with all Information required to show the widget */
    @State() data: QuickOrderResponse = null;
    /** width of the element - this helps with styling. Media queries can not be used because the width of the element can be independent from width of the window */
    @State() width: number = 400;
    /** data is loading */
    @State() loading: boolean = false;

    @Element() domNode: HTMLUxWQuickOrderElement;

    @Watch("url")
    componentWillLoad() {
        if (this.url === "") {
            // eslint-disable-next-line no-console
            console.warn("url needs to be supplied for WidgetQuickOrder");
            return;
        }
        window.setTimeout(() => {
            if (this.data === null) {
                this.loading = true;
            }
        });
        api<QuickOrderResponse>(this.url)
            .then((data: QuickOrderResponse) => {
                this.data = data;
                this.loading = false;
            })
            // eslint-disable-next-line no-console
            .catch((error) => console.error(error));

        this.setWidth();
        window.onresize = debounce(() => this.setWidth(), 300);
    }

    private setWidth() {
        const width =
            this.domNode?.parentElement?.getBoundingClientRect().width;
        if (width > 0) {
            this.width = width;
        }
    }

    private onSubmit(event) {
        event.preventDefault();
        const textInput = this.domNode.querySelector("input[type=text]");
        const numberInput = this.domNode.querySelector("input[type=number]");
        const text = (textInput as HTMLInputElement).value;
        const number = (numberInput as HTMLInputElement).value;
        const { amountParamName, materialNumberParamName, orderUrl } =
            this.data;
        if (
            orderUrl?.length < 1 ||
            amountParamName?.length < 1 ||
            materialNumberParamName?.length < 1
        ) {
            // eslint-disable-next-line no-console
            console.error(
                "Widget Quick Order API needs to provide valid orderUrl, amountParamName and materialNumberParamName"
            );
            return;
        }
        window.location.href = buildUrl(orderUrl, {
            [amountParamName]: number,
            [materialNumberParamName]: text
        });
    }

    render() {
        const baseClass = "ux-w-quick-order";
        if (this.loading) {
            return (
                <Host class={baseClass}>
                    <ux-w-wrapper isLoading />
                </Host>
            );
        }
        if (this.data === null) {
            return null;
        }
        const {
            link,
            linkTitle,
            title,
            inputLabel,
            inputPlaceholder,
            suggestionUrl,
            orderButton,
            noData,
            noDataText
        } = this.data;
        const { width } = this;
        const isSmall = width <= 380;
        const isMedium = width <= 740;

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--small`]: isSmall,
                    [`${baseClass}--medium`]: isMedium
                }}
            >
                <ux-w-wrapper url={link} linkTitle={linkTitle} headline={title}>
                    <div slot="content">
                        {noData && (
                            <ux-w-empty icon="crane">
                                <div innerHTML={noDataText} />
                            </ux-w-empty>
                        )}
                        {!noData && (
                            <form
                                class={`${baseClass}__form`}
                                onSubmit={(event) => this.onSubmit(event)}
                            >
                                <div class={`${baseClass}__text`}>
                                    <ux-autosuggest-input
                                        label={inputLabel}
                                        placeholder={inputPlaceholder}
                                        url={suggestionUrl}
                                    />
                                </div>
                                <div class={`${baseClass}__number`}>
                                    <ux-number-input
                                        min="1"
                                        max="99"
                                        initialValue="1"
                                    />
                                </div>
                                <div class={`${baseClass}__button`}>
                                    <ux-button
                                        variant={"primary" as ButtonVariant}
                                        size={
                                            isSmall
                                                ? ("icon" as ButtonSize)
                                                : undefined
                                        }
                                        type="submit"
                                        title={orderButton}
                                    >
                                        {isSmall ? (
                                            <ux-icon slot="icon" name="cart" />
                                        ) : (
                                            orderButton
                                        )}
                                    </ux-button>
                                </div>
                            </form>
                        )}
                    </div>
                </ux-w-wrapper>
            </Host>
        );
    }
}
